import { httpClient } from '@bs/core'
import type {
  LoyaltyCampaignPerformance,
  LoyaltyConsentInput,
  LoyaltyConsentResult,
  LoyaltyCustomerActivityParams,
  LoyaltyCustomerActivityResponse,
  LoyaltyCustomerAnalytics,
  LoyaltyDevicesAnalytics,
  LoyaltyGeoRow,
  LoyaltyMetrics,
  LoyaltyMetricsRange,
  LoyaltyProfilesParams,
  LoyaltyProfilesResponse,
  LoyaltySearchTerm,
  LoyaltySessionsAnalytics,
  LoyaltyTopEarner,
  LoyaltyTopPage,
} from '../../types/loyaltyTracking'

// httpClient.json.* unwraps res.data.data → the resolved value is the inner
// `data` payload directly (SuccessResponse envelope already stripped). The
// org + store headers are injected automatically, so these calls resolve to the
// same be_api the rest of the loyalty surface uses. The Tracking wire shape is
// snake_case and matches the type defs in `types/loyaltyTracking.ts`, so
// payloads pass through untouched (no snake↔camel mapper needed).

// ── URL builders ──────────────────────────────────────────────────────────────

const loyaltyBase = (storeId: string) => `/stores/${storeId}/loyalty`
const metricsUrl = (storeId: string) => `${loyaltyBase(storeId)}/metrics`
const analyticsUrl = (storeId: string) => `${loyaltyBase(storeId)}/analytics`
const customerAnalyticsUrl = (storeId: string, customerId: string) =>
  `${loyaltyBase(storeId)}/customers/${customerId}/analytics`
const profilesUrl = (storeId: string) => `${loyaltyBase(storeId)}/profiles`
const consentUrl = (storeId: string) => `${profilesUrl(storeId)}/consent`
const customerActivityUrl = (storeId: string, customerId: string) =>
  `${loyaltyBase(storeId)}/customers/${customerId}/activity`
const anonymousActivityUrl = (storeId: string, anonymousId: string) =>
  `${loyaltyBase(storeId)}/anonymous/${anonymousId}/activity`

// ── Analytics / metrics ──────────────────────────────────────────────────────

export const getLoyaltyMetrics = async (
  storeId: string,
  range: LoyaltyMetricsRange,
): Promise<LoyaltyMetrics> => {
  const raw = (await httpClient.json.get(metricsUrl(storeId), {
    params: { range },
  })) as LoyaltyMetrics | null
  return {
    range: raw?.range ?? range,
    revenue: typeof raw?.revenue === 'number' ? raw.revenue : 0,
    currency: raw?.currency ?? 'USD',
    series: raw?.series ?? [],
    top_metrics: raw?.top_metrics ?? [],
    orders_total: typeof raw?.orders_total === 'number' ? raw.orders_total : 0,
    checkouts_total:
      typeof raw?.checkouts_total === 'number' ? raw.checkouts_total : 0,
    conversion_rate:
      typeof raw?.conversion_rate === 'number' ? raw.conversion_rate : 0,
    metric_names: raw?.metric_names ?? [],
    metric_series: raw?.metric_series ?? [],
    top_products: raw?.top_products ?? [],
    top_viewed_products: raw?.top_viewed_products ?? [],
  }
}

// ── ClickHouse-backed analytics (spec §2.1) ──────────────────────────────────
// Six read-only endpoints under `stores/{storeId}/loyalty/analytics/*` (+ the
// per-customer `customers/{cid}/analytics`). All take `range=7d|30d|90d`; the
// wire shapes below mirror the tracking-module design spec exactly.

export const getLoyaltySessions = async (
  storeId: string,
  range: LoyaltyMetricsRange,
): Promise<LoyaltySessionsAnalytics> => {
  const raw = (await httpClient.json.get(`${analyticsUrl(storeId)}/sessions`, {
    params: { range },
  })) as LoyaltySessionsAnalytics | null
  return {
    sessions_total:
      typeof raw?.sessions_total === 'number' ? raw.sessions_total : 0,
    avg_duration_seconds:
      typeof raw?.avg_duration_seconds === 'number'
        ? raw.avg_duration_seconds
        : 0,
    pages_per_session:
      typeof raw?.pages_per_session === 'number' ? raw.pages_per_session : 0,
    bounce_rate: typeof raw?.bounce_rate === 'number' ? raw.bounce_rate : 0,
    series: raw?.series ?? [],
  }
}

export const getLoyaltyTopEarners = async (
  storeId: string,
  range: LoyaltyMetricsRange,
  limit = 10,
): Promise<LoyaltyTopEarner[]> => {
  const raw = (await httpClient.json.get(
    `${analyticsUrl(storeId)}/top-earners`,
    { params: { range, limit } },
  )) as LoyaltyTopEarner[] | { top_earners?: LoyaltyTopEarner[] } | null
  if (Array.isArray(raw)) return raw
  return raw?.top_earners ?? []
}

export const getLoyaltySearchTerms = async (
  storeId: string,
  range: LoyaltyMetricsRange,
  limit = 20,
): Promise<LoyaltySearchTerm[]> => {
  const raw = (await httpClient.json.get(
    `${analyticsUrl(storeId)}/search-terms`,
    { params: { range, limit } },
  )) as LoyaltySearchTerm[] | { terms?: LoyaltySearchTerm[] } | null
  if (Array.isArray(raw)) return raw
  return raw?.terms ?? []
}

export const getLoyaltyTopPages = async (
  storeId: string,
  range: LoyaltyMetricsRange,
  limit = 20,
): Promise<LoyaltyTopPage[]> => {
  const raw = (await httpClient.json.get(`${analyticsUrl(storeId)}/top-pages`, {
    params: { range, limit },
  })) as LoyaltyTopPage[] | { pages?: LoyaltyTopPage[] } | null
  if (Array.isArray(raw)) return raw
  return raw?.pages ?? []
}

export const getLoyaltyCampaignPerformance = async (
  storeId: string,
  range: LoyaltyMetricsRange,
  limit = 5,
): Promise<LoyaltyCampaignPerformance[]> => {
  const raw = (await httpClient.json.get(`${analyticsUrl(storeId)}/campaigns`, {
    params: { range, limit },
  })) as
    | LoyaltyCampaignPerformance[]
    | { campaigns?: LoyaltyCampaignPerformance[] }
    | null
  if (Array.isArray(raw)) return raw
  return raw?.campaigns ?? []
}

export const getLoyaltyDevices = async (
  storeId: string,
  range: LoyaltyMetricsRange,
): Promise<LoyaltyDevicesAnalytics> => {
  const raw = (await httpClient.json.get(`${analyticsUrl(storeId)}/devices`, {
    params: { range },
  })) as LoyaltyDevicesAnalytics | null
  return {
    categories: raw?.categories ?? [],
    browsers: raw?.browsers ?? [],
    os: raw?.os ?? [],
  }
}

// Without `country` the rows are per-country; with `country` per-region for
// that country (US/CA drive the state/province choropleth, others the table).
export const getLoyaltyGeo = async (
  storeId: string,
  range: LoyaltyMetricsRange,
  country?: string,
): Promise<LoyaltyGeoRow[]> => {
  const raw = (await httpClient.json.get(`${analyticsUrl(storeId)}/geo`, {
    params: { range, ...(country ? { country } : {}) },
  })) as
    | LoyaltyGeoRow[]
    | { countries?: LoyaltyGeoRow[]; regions?: LoyaltyGeoRow[] }
    | null
  if (Array.isArray(raw)) return raw
  return raw?.countries ?? raw?.regions ?? []
}

export const getLoyaltyCustomerAnalytics = async (
  storeId: string,
  customerId: string,
  range: LoyaltyMetricsRange,
): Promise<LoyaltyCustomerAnalytics> => {
  const raw = (await httpClient.json.get(
    customerAnalyticsUrl(storeId, customerId),
    { params: { range } },
  )) as LoyaltyCustomerAnalytics | null
  return {
    top_purchased_products: raw?.top_purchased_products ?? [],
    sessions_total:
      typeof raw?.sessions_total === 'number' ? raw.sessions_total : 0,
    avg_session_seconds:
      typeof raw?.avg_session_seconds === 'number'
        ? raw.avg_session_seconds
        : 0,
    total_events: typeof raw?.total_events === 'number' ? raw.total_events : 0,
    first_seen: raw?.first_seen ?? null,
    last_seen: raw?.last_seen ?? null,
    categories: raw?.categories ?? [],
  }
}

// ── Profiles list ────────────────────────────────────────────────────────────

export const getLoyaltyProfiles = async (
  storeId: string,
  params: LoyaltyProfilesParams,
): Promise<LoyaltyProfilesResponse> => {
  const raw = (await httpClient.json.get(profilesUrl(storeId), {
    params: {
      ...(params.search ? { search: params.search } : {}),
      ...(typeof params.limit === 'number' ? { limit: params.limit } : {}),
      ...(params.cursor ? { cursor: params.cursor } : {}),
      ...(params.exclude_unsubscribed ? { exclude_unsubscribed: true } : {}),
      ...(params.filter ? { filter: params.filter } : {}),
    },
  })) as LoyaltyProfilesResponse | null
  return {
    profiles: raw?.profiles ?? [],
    total_items: raw?.total_items ?? 0,
    next_cursor: raw?.next_cursor ?? null,
  }
}

export const setLoyaltyProfileConsent = async (
  storeId: string,
  input: LoyaltyConsentInput,
): Promise<LoyaltyConsentResult> => {
  const raw = (await httpClient.json.post(
    consentUrl(storeId),
    input,
  )) as LoyaltyConsentResult | null
  return {
    email: raw?.email ?? input.email,
    subscribed: raw?.subscribed ?? input.subscribed,
  }
}

// ── Per-customer activity feed ───────────────────────────────────────────────

export const getLoyaltyCustomerActivity = async (
  storeId: string,
  customerId: string,
  params: LoyaltyCustomerActivityParams = {},
): Promise<LoyaltyCustomerActivityResponse> => {
  const raw = (await httpClient.json.get(
    customerActivityUrl(storeId, customerId),
    {
      params: {
        ...(typeof params.limit === 'number' ? { limit: params.limit } : {}),
        ...(params.before ? { before: params.before } : {}),
        ...(params.category ? { category: params.category } : {}),
      },
    },
  )) as LoyaltyCustomerActivityResponse | null
  return {
    events: raw?.events ?? [],
    next_cursor: raw?.next_cursor ?? null,
  }
}

// ── Per-anonymous-session activity feed ──────────────────────────────────────
// Same shape as the customer feed, keyed by anonymous_id (never-identified
// storefront sessions). Backs the Activity dialog for anonymous profile rows.

export const getLoyaltyAnonymousActivity = async (
  storeId: string,
  anonymousId: string,
  params: LoyaltyCustomerActivityParams = {},
): Promise<LoyaltyCustomerActivityResponse> => {
  const raw = (await httpClient.json.get(
    anonymousActivityUrl(storeId, anonymousId),
    {
      params: {
        ...(typeof params.limit === 'number' ? { limit: params.limit } : {}),
        ...(params.before ? { before: params.before } : {}),
        ...(params.category ? { category: params.category } : {}),
      },
    },
  )) as LoyaltyCustomerActivityResponse | null
  return {
    events: raw?.events ?? [],
    next_cursor: raw?.next_cursor ?? null,
  }
}
